/**
 * English / Arabic. English text is the key; the Arabic dictionary (ar/*.ts) maps it to
 * Arabic. Anything not yet translated shows in English. Placeholders: "{name}".
 */
import { AR } from './ar/index.js';

export const LANGUAGES = ['en', 'ar'] as const;
export type Language = (typeof LANGUAGES)[number];
export type Dict = Record<string, string>;
export type Vars = Record<string, string | number | null | undefined>;

/**
 * Texts that come from the server with values already filled in (e.g. "Close the reporting
 * period September 2026 first…") are matched against dictionary keys that contain
 * placeholders ("Close the reporting period {label} first…"); the values are carried over.
 */
let patterns: { re: RegExp; names: string[]; ar: string }[] | null = null;
let compiledFor = 0;
function compilePatterns() {
  patterns = [];
  compiledFor = Object.keys(AR).length;
  for (const [key, ar] of Object.entries(AR)) {
    if (!key.includes('{') || key.length > 600) continue;
    // Only sentence-like keys: short keys such as "{n}h" would match unrelated text.
    const literal = key.replace(/\{\w+\}/g, '');
    if (literal.replace(/\s+/g, '').length < 12 || !/\s/.test(literal.trim())) continue;
    const names: string[] = [];
    const src = key
      .split(/(\{\w+\})/)
      .map((part) => {
        const m = /^\{(\w+)\}$/.exec(part);
        if (m) {
          names.push(m[1]!);
          return '(.+?)';
        }
        return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      })
      .join('');
    if (!names.length) continue;
    patterns.push({ re: new RegExp(`^${src}$`, 's'), names, ar });
  }
  // Most specific (longest literal text) first.
  patterns.sort((a, b) => b.re.source.length - a.re.source.length);
}
function fromPattern(text: string): string | null {
  if (text.length > 1000) return null;
  if (!patterns || compiledFor !== Object.keys(AR).length) compilePatterns();
  for (const p of patterns!) {
    const m = p.re.exec(text);
    if (m) return p.ar.replace(/\{(\w+)\}/g, (all, k: string) => {
      const i = p.names.indexOf(k);
      return i >= 0 ? translateValue(m[i + 1]!) : all;
    });
  }
  return null;
}
/** A value inside a sentence may itself be translatable (e.g. a month name, a status). */
function translateValue(v: string): string {
  return AR[v] ?? localizeMonths(v);
}
const MONTHS: [string, string][] = [
  ['January', 'يناير'], ['February', 'فبراير'], ['March', 'مارس'], ['April', 'أبريل'], ['May', 'مايو'], ['June', 'يونيو'],
  ['July', 'يوليو'], ['August', 'أغسطس'], ['September', 'سبتمبر'], ['October', 'أكتوبر'], ['November', 'نوفمبر'], ['December', 'ديسمبر'],
];
const DAYS: [string, string][] = [
  ['Monday', 'الاثنين'], ['Tuesday', 'الثلاثاء'], ['Wednesday', 'الأربعاء'], ['Thursday', 'الخميس'], ['Friday', 'الجمعة'], ['Saturday', 'السبت'], ['Sunday', 'الأحد'],
];
/**
 * "September 2026" → "سبتمبر 2026"; also short dates the server writes into sentences
 * ("Mon 21 Sep", "05 Oct 2026"). Period names are stored in English.
 */
export function localizeMonths(s: string): string {
  let out = s;
  for (const [en, ar] of MONTHS) out = out.replace(new RegExp(`\\b${en}\\b`, 'g'), ar);
  for (const [en, ar] of DAYS) out = out.replace(new RegExp(`\\b${en}\\b`, 'g'), ar);
  // Short forms only next to a number, so ordinary words (e.g. "may") are left alone.
  for (const [en, ar] of MONTHS) out = out.replace(new RegExp(`(\\d\\s)${en.slice(0, 3)}\\b|\\b${en.slice(0, 3)}(?=\\s\\d)`, 'g'), (m, pre) => `${pre ?? ''}${ar}`);
  for (const [en, ar] of DAYS) out = out.replace(new RegExp(`\\b${en.slice(0, 3)}(?=\\s\\d)`, 'g'), ar);
  return out;
}

export function translate(lang: Language | string | null | undefined, text: string, vars?: Vars): string {
  let s = lang === 'ar' ? (AR[text] ?? fromPattern(text) ?? text) : text;
  if (vars) s = s.replace(/\{(\w+)\}/g, (m, k: string) => (vars[k] === undefined || vars[k] === null ? m : String(vars[k])));
  return s;
}

/** True when an Arabic translation exists (used by the missing-translations check). */
export function hasArabic(text: string): boolean {
  return Object.prototype.hasOwnProperty.call(AR, text);
}

export const isRtl = (lang: string | null | undefined) => lang === 'ar';

/** Durations: "6h 42m" / "6 س 42 د". */
export function formatMinutesIn(lang: Language | string, total: number | null | undefined): string {
  if (total == null || Number.isNaN(total)) return '—';
  const sign = total < 0 ? '−' : '';
  const abs = Math.abs(Math.round(total));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (lang === 'ar') return h === 0 ? `${sign}${m} د` : `${sign}${h} س ${String(m).padStart(2, '0')} د`;
  return h === 0 ? `${sign}${m}m` : `${sign}${h}h ${String(m).padStart(2, '0')}m`;
}

export { AR };
